import { projectsApi } from '@/api/projects.api';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { type DrillSide, ORIENTATION_ORDER } from '@/types/detection.types';
import { downloadBlob } from '@/utils/download';
import { ChevronDown, ChevronUp, Download, FileText, RefreshCw } from 'lucide-react';
import { Button } from 'primereact/button';
import { TabPanel, TabView } from 'primereact/tabview';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

const PREVIEW_MAX_ROWS = 50;

/** Label row that starts the procedure table (body); rows above are header. */
const PROCEDURE_HEADER_LABEL = 'Název procedury';

export interface NCPreviewData {
  sheetNames: string[];
  headerRowCount: number;
  sheetRows: Record<string, string[][]>;
}

function parseNcPreviewAllSheets(blob: Blob): Promise<NCPreviewData> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const ab = e.target?.result;
        if (!(ab instanceof ArrayBuffer)) {
          reject(new Error('Invalid result'));
          return;
        }
        const wb = XLSX.read(ab, { type: 'array' });
        const sheetNames = wb.SheetNames ?? [];
        const sheetRows: Record<string, string[][]> = {};

        for (const name of sheetNames) {
          const ws = wb.Sheets[name];
          const rows = XLSX.utils.sheet_to_json<string[]>(ws, {
            header: 1,
            defval: '',
            raw: false,
          }) as string[][];
          sheetRows[name] = rows.slice(0, PREVIEW_MAX_ROWS);
        }

        let headerRowCount = 0;
        const firstSheetRows = sheetNames[0] ? (sheetRows[sheetNames[0]] ?? []) : [];
        for (let i = 0; i < firstSheetRows.length; i++) {
          const row = firstSheetRows[i];
          const hasProcedureHeader = row?.some((cell) => String(cell).includes(PROCEDURE_HEADER_LABEL));
          if (hasProcedureHeader) {
            headerRowCount = i;
            break;
          }
        }

        resolve({ sheetNames, headerRowCount, sheetRows });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

interface NCSheetPreviewProps {
  rows: string[][];
  headerRowCount: number;
  headerCollapsed: boolean;
  onToggleHeader: () => void;
  t: (key: string, opts?: { count?: number }) => string;
}

const NCSheetPreview: FC<NCSheetPreviewProps> = ({ rows, headerRowCount, headerCollapsed, onToggleHeader, t }) => {
  const headerRows = headerRowCount > 0 ? rows.slice(0, headerRowCount) : [];
  const bodyRows = rows.slice(headerRowCount);
  const hasHeader = headerRows.length > 0;

  return (
    <div className="w-full overflow-auto rounded border border-gray-200 max-h-[500px]">
      <table className="w-full text-sm border-collapse">
        <tbody>
          {hasHeader && (
            <tr
              role="button"
              tabIndex={0}
              onClick={onToggleHeader}
              onKeyDown={(e) => e.key === 'Enter' && onToggleHeader()}
              className="bg-gray-100 hover:bg-gray-200 cursor-pointer border-b border-gray-200"
            >
              <td colSpan={10} className="px-3 py-2 whitespace-nowrap">
                <span className="inline-flex items-center gap-2">
                  {headerCollapsed ? (
                    <ChevronDown size={16} className="text-gray-500 shrink-0" aria-hidden />
                  ) : (
                    <ChevronUp size={16} className="text-gray-500 shrink-0" aria-hidden />
                  )}
                  {t('projectDetail.ncDocumentHeaderLabel')}
                </span>
              </td>
            </tr>
          )}
          {!headerCollapsed &&
            hasHeader &&
            headerRows.map((row, i) => (
              <tr key={i} className={i === 0 ? 'bg-gray-50 font-medium' : 'hover:bg-gray-50/50'}>
                {row.map((cell, j) => (
                  <td key={j} className="px-3 py-2 border-b border-gray-100 whitespace-nowrap">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          {bodyRows.map((row, i) => (
            <tr key={i} className="hover:bg-gray-50/50">
              {row.map((cell, j) => (
                <td key={j} className="px-3 py-2 border-b border-gray-100 whitespace-nowrap">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface NCDocumentPanelProps {
  projectId: string;
  projectTitle?: string;
  /** When this value changes, NC document is reloaded (e.g. after refresh/regeneration). */
  refreshKey?: number;
  /** When false, no request to nc-document/{project_id}/download is sent until it becomes true (e.g. after all dependent sections including APT Generation are ready). Default false so loading never starts without explicit parent consent. */
  canLoad?: boolean;
  /** When true, the preview content (TabView) is blurred and the Download button is disabled. */
  blurred?: boolean;
}

type LoadStatus = 'idle' | 'loading' | 'success' | 'error';

export const NCDocumentPanel: FC<NCDocumentPanelProps> = ({
  projectId,
  projectTitle,
  refreshKey = 0,
  canLoad = false,
  blurred = false,
}) => {
  const { t } = useTranslation();
  const [loadStatus, setLoadStatus] = useState<LoadStatus>('idle');
  const [ncBlob, setNcBlob] = useState<Blob | null>(null);
  const [previewData, setPreviewData] = useState<NCPreviewData | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [headerCollapsed, setHeaderCollapsed] = useState(true);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);

  const loadNcDocument = useCallback(async () => {
    setLoadStatus('loading');
    try {
      const blob = await projectsApi.downloadNCDocument(projectId);
      setNcBlob(blob);
      const data = await parseNcPreviewAllSheets(blob);
      setPreviewData(data);
      setActiveSheetIndex(0);
      setLoadStatus('success');
    } catch (_error) {
      setLoadStatus('error');
    }
  }, [projectId]);

  useEffect(() => {
    if (!canLoad) return;
    loadNcDocument();
  }, [loadNcDocument, refreshKey, canLoad]);

  const handleRegenerateNC = async () => {
    setIsRegenerating(true);
    try {
      await projectsApi.regenerateNCDocument(projectId);
      toast.success(t('projectDetail.ncDocumentRegenerateSuccess'));
      await loadNcDocument();
    } catch (_error) {
      toast.error(t('projectDetail.ncDocumentRegenerateError'));
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleDownloadNC = async () => {
    if (ncBlob) {
      setIsDownloading(true);
      try {
        const filename = `${projectTitle || projectId}_nc_document.xlsm`;
        downloadBlob(ncBlob, filename);
        toast.success(t('projectDetail.ncDocumentDownloadSuccess'));
      } finally {
        setIsDownloading(false);
      }
      return;
    }
    setIsDownloading(true);
    try {
      const blob = await projectsApi.downloadNCDocument(projectId);
      const filename = `${projectTitle || projectId}_nc_document.xlsm`;
      downloadBlob(blob, filename);
      toast.success(t('projectDetail.ncDocumentDownloadSuccess'));
    } catch (_error) {
      toast.error(t('projectDetail.ncDocumentDownloadError'));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FileText size={24} className="text-primary" />
          <h2 className="text-xl font-semibold text-gray-900">{t('projectDetail.ncDocument')}</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            label={t('projectDetail.regenerateNC')}
            icon={<RefreshCw size={16} className="mr-2" />}
            onClick={handleRegenerateNC}
            loading={isRegenerating}
            disabled={isRegenerating || isDownloading || blurred || loadStatus === 'loading'}
            outlined={true}
            size="small"
            aria-label={t('projectDetail.regenerateNC')}
          />
          <Button
            label={t('projectDetail.downloadNCDocument')}
            icon={<Download size={16} className="mr-2" />}
            onClick={handleDownloadNC}
            loading={isDownloading}
            disabled={isDownloading || isRegenerating || blurred || loadStatus === 'loading'}
            outlined={true}
            size="small"
            aria-label={t('projectDetail.downloadNCDocument')}
          />
        </div>
      </div>

      {/* Content: loading / preview / empty */}
      <div
        className={`flex flex-col items-center justify-center min-h-[400px] gap-4 relative ${blurred ? 'select-none' : ''}`}
        aria-hidden={blurred}
      >
        {blurred && (
          <div
            className="absolute inset-0 z-10 backdrop-blur-sm bg-white/60 pointer-events-auto rounded-lg"
            aria-hidden
          />
        )}
        {loadStatus === 'loading' && (
          <div className="flex flex-col items-center gap-4">
            <LoadingSpinner size="large" />
            <p className="text-sm text-gray-500">{t('projectDetail.ncDocumentLoading')}</p>
          </div>
        )}

        {loadStatus === 'error' && (
          <EmptyState title={t('projectDetail.ncDocument')} message={t('projectDetail.ncDocumentArea')} />
        )}

        {loadStatus === 'success' && previewData && (
          <>
            {previewData.sheetNames.length > 0 ? (
              <div className="w-full flex flex-col gap-3">
                {(() => {
                  const sortedSheetNames = [...previewData.sheetNames].sort((a, b) => {
                    const orientationA = a.replace(/^NC_/, '');
                    const orientationB = b.replace(/^NC_/, '');
                    const iA = ORIENTATION_ORDER.indexOf(orientationA as DrillSide);
                    const iB = ORIENTATION_ORDER.indexOf(orientationB as DrillSide);
                    if (iA === -1 && iB === -1) return a.localeCompare(b);
                    if (iA === -1) return 1;
                    if (iB === -1) return -1;
                    return iA - iB;
                  });
                  const firstSheetName = sortedSheetNames[0];
                  const headerRows =
                    firstSheetName && previewData.headerRowCount > 0
                      ? (previewData.sheetRows[firstSheetName] ?? []).slice(0, previewData.headerRowCount)
                      : [];
                  const hasHeader = headerRows.length > 0;
                  return hasHeader ? (
                    <div className="w-full overflow-auto rounded border border-gray-200">
                      <table className="w-full text-sm border-collapse">
                        <tbody>
                          <tr
                            role="button"
                            tabIndex={0}
                            onClick={() => setHeaderCollapsed((c) => !c)}
                            onKeyDown={(e) => e.key === 'Enter' && setHeaderCollapsed((c) => !c)}
                            className="bg-gray-100 hover:bg-gray-200 cursor-pointer border-b border-gray-200"
                          >
                            <td colSpan={10} className="px-3 py-2 whitespace-nowrap">
                              <span className="inline-flex items-center gap-2">
                                {headerCollapsed ? (
                                  <ChevronDown size={16} className="text-gray-500 shrink-0" aria-hidden />
                                ) : (
                                  <ChevronUp size={16} className="text-gray-500 shrink-0" aria-hidden />
                                )}
                                {t('projectDetail.ncDocumentHeaderLabel')}
                              </span>
                            </td>
                          </tr>
                          {!headerCollapsed &&
                            headerRows.map((row, i) => (
                              <tr key={i} className={i === 0 ? 'bg-gray-50 font-medium' : 'hover:bg-gray-50/50'}>
                                {row.map((cell, j) => (
                                  <td key={j} className="px-3 py-2 border-b border-gray-100 whitespace-nowrap">
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null;
                })()}
                <TabView
                  activeIndex={activeSheetIndex}
                  onTabChange={(e) => setActiveSheetIndex(e.index)}
                  className="[&_.p-tablist]:flex [&_.p-tablist]:gap-1 [&_.p-tablist]:border-b [&_.p-tablist]:border-gray-200"
                >
                  {[...previewData.sheetNames]
                    .sort((a, b) => {
                      const orientationA = a.replace(/^NC_/, '');
                      const orientationB = b.replace(/^NC_/, '');
                      const iA = ORIENTATION_ORDER.indexOf(orientationA as DrillSide);
                      const iB = ORIENTATION_ORDER.indexOf(orientationB as DrillSide);
                      if (iA === -1 && iB === -1) return a.localeCompare(b);
                      if (iA === -1) return 1;
                      if (iB === -1) return -1;
                      return iA - iB;
                    })
                    .map((sheetName) => (
                      <TabPanel key={sheetName} header={sheetName.replace(/^NC_/, '')} headerClassName="text-sm">
                        <NCSheetPreview
                          rows={(previewData.sheetRows[sheetName] ?? []).slice(previewData.headerRowCount)}
                          headerRowCount={0}
                          headerCollapsed={headerCollapsed}
                          onToggleHeader={() => setHeaderCollapsed((c) => !c)}
                          t={t}
                        />
                      </TabPanel>
                    ))}
                </TabView>
                {Object.values(previewData.sheetRows)[0]?.length >= PREVIEW_MAX_ROWS && (
                  <p className="text-xs text-gray-400">
                    {t('projectDetail.ncDocumentPreviewLimit', { max: PREVIEW_MAX_ROWS })}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">{t('projectDetail.ncDocumentNoPreview')}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};
